import React, { FunctionComponent, useRef, useContext } from 'react';
import styled, { css } from 'styled-components';
import { AgreementModalProps } from './AgreementModal.types';
import { Modal, Button, Group } from '../../index';
import { withAlpha } from '../../../util/color.util';
import { BrandStore } from '../../../context';
import { useWindowSize } from '../../../hooks';
import { PdfSection } from './PdfSection';

const Body = styled.div`
  font-size: 12px;
  margin: 1rem 2.5%;
  max-height: calc(100% - 72px);
  overflow: hidden;
  display: flex;
  flex-direction: column;

  > * {
    margin-bottom: 1rem;
  }
`;

const ContentCss = css`
  padding: 1%;
  border: 1px solid lightgray;
  border-radius: 6px;

  ol {
    padding-left: 1rem;
  }

  li {
    margin-bottom: 0.5rem;

    &:first-child {
      margin-top: 0.5rem;
    }
  }
`;

const TableOfContents = styled.div`
  ${ContentCss}
`;

const ScrollableContent = styled.div`
  ${ContentCss}
  flex: 0 1;
  overflow-y: scroll;
`;

const Alert = styled.div`
  padding: 1rem;
  border: 1px solid ${props => props.theme.colors.primaryAccent};
  border-radius: 6px;
  color: ${props => props.theme.colors.primaryAccent};
  background: ${props => withAlpha(props.theme.colors.primaryAccent, 0.1)};
`;

const scrollToRef = (ref: React.RefObject<HTMLElement>) =>
  ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

export const AgreementModal: FunctionComponent<AgreementModalProps> = props => {
  const merchantAppCompanyInfoRef = useRef<HTMLDivElement>(null);
  const merchantAppScheduleARef = useRef<HTMLDivElement>(null);
  const termsAndConditionsRef = useRef<HTMLDivElement>(null);
  const fattPromiseRef = useRef<HTMLDivElement>(null);

  const { isMobile } = useWindowSize();
  const {
    state: { brand },
  } = useContext(BrandStore);

  const shouldShowTermsAndConditionsFattPromise =
    brand && brand.shouldShowTermsAndConditionsFattPromise;

  const candidateFees = [
    'inquiryFee',
    'chargebackFee',
    'returnAchFee',
    'minimumMerchantBill',
    'pciNonComplianceFee',
    'pciAnnualFee',
    'pciMonthlyFee',
    'onFileFee',
    'annualFee',
    'surchargePlatformFee',
    'accountUpdaterFee',
    'subscriptionFee',
    'performanceAnalyticsFee',
  ];

  const brandMiscFees = {};
  candidateFees.forEach(fee => {
    if (brand && brand[fee]) {
      brandMiscFees[fee] = brand[fee];
    }
  });

  return (
    <Modal
      title="Terms &amp; Conditions"
      id="agreement-modal"
      data-testid="agreement-modal"
      width="90%"
      style={{ height: '90%' }}
      {...props}
    >
      <Body>
        <Alert>
          <strong>
            <i className="fas fa-exclamation-triangle" /> Important:
          </strong>{' '}
          <span>
            In order to continue, you must read the Terms &amp; Conditions
            {shouldShowTermsAndConditionsFattPromise && ' and Stax Promise'} by
            scrolling to the bottom.
          </span>
        </Alert>
        <TableOfContents>
          <span>Navigate to:</span>
          <ol type="i">
            <li>
              <Button
                variant="link"
                onClick={() => scrollToRef(merchantAppCompanyInfoRef)}
              >
                {isMobile
                  ? 'Merchant App - Company Info'
                  : 'Merchant Application - Company Info'}
              </Button>
            </li>
            <li>
              <Button
                variant="link"
                onClick={() => scrollToRef(merchantAppScheduleARef)}
              >
                {isMobile
                  ? 'Merchant App - Schedule A'
                  : 'Merchant Application - Schedule A'}
              </Button>
            </li>
            <li>
              <Button
                variant="link"
                onClick={() => scrollToRef(termsAndConditionsRef)}
              >
                Terms &amp; Conditions
              </Button>
            </li>
            {shouldShowTermsAndConditionsFattPromise && (
              <li>
                <Button
                  variant="link"
                  onClick={() => scrollToRef(fattPromiseRef)}
                >
                  Stax Promise
                </Button>
              </li>
            )}
          </ol>
        </TableOfContents>
        <ScrollableContent>
          <div>
            <PdfSection
              title="Merchant Application - Company Info"
              type="merchant_company_information"
              ref={merchantAppCompanyInfoRef}
            />
            <PdfSection
              title="Merchant Application - Schedule A"
              type="stax_application"
              ref={merchantAppScheduleARef}
            />
            <PdfSection
              title="Terms & Conditions"
              type="terms_and_conditions"
              ref={termsAndConditionsRef}
            />
            {shouldShowTermsAndConditionsFattPromise && (
              <PdfSection
                title="Stax Promise"
                type="stax_promise"
                ref={fattPromiseRef}
              />
            )}
          </div>
        </ScrollableContent>
        <Group justify="end">
          <Button type="submit" onClick={props.onClose}>
            <span>Close</span>
          </Button>
        </Group>
      </Body>
    </Modal>
  );
};
